import Supercluster from 'supercluster'
import { AlertStatus, DeviceStatus, IUserBbox } from '@/api/user/getUsersBbox.ts'

export const MAX_CLUSTER_ZOOM = 21
export const CLUSTER_MEMBER_LIST_LIMIT = 50

const CLUSTER_RADIUS_PX = 60
// 구글 맵 타일(256px)과 맞춰야 radius가 화면 픽셀과 일치한다.
const CLUSTER_TILE_EXTENT = 256
const WORLD_BBOX: [number, number, number, number] = [-180, -90, 180, 90]

const ALERT_RANKS = [AlertStatus.NONE, AlertStatus.BIO, AlertStatus.EMERGENCY]
const DEVICE_RANKS = [DeviceStatus.OFFLINE, DeviceStatus.CHARGING, DeviceStatus.ONLINE, DeviceStatus.EMERGENCY]

export const getAlertRank = (user: IUserBbox) => Math.max(0, ALERT_RANKS.indexOf(user.todayAlert))
export const getAlertStatusByRank = (rank: number) => ALERT_RANKS[rank] ?? AlertStatus.NONE

export const getDeviceRank = (user: IUserBbox) => Math.max(0, DEVICE_RANKS.indexOf(user.deviceStatus))
export const getDeviceStatusByRank = (rank: number) => DEVICE_RANKS[rank] ?? DeviceStatus.OFFLINE

interface UserProps {
    user: IUserBbox
    rank: number
}

interface RankProps {
    rank: number
}

export type ClusterIndex = Supercluster<UserProps, RankProps>

export interface ClusterMarker {
    key: string
    lat: number
    lng: number
    count: number
    rank: number
    clusterId?: number
    user?: IUserBbox
}

export interface ClusterSelection {
    marker: ClusterMarker
    users: IUserBbox[]
    zoomTo?: number
}

export const createClusterIndex = (users: IUserBbox[], getRank: (user: IUserBbox) => number): ClusterIndex => {
    const index: ClusterIndex = new Supercluster<UserProps, RankProps>({
        radius: CLUSTER_RADIUS_PX,
        extent: CLUSTER_TILE_EXTENT,
        maxZoom: MAX_CLUSTER_ZOOM,
        minPoints: 2,
        map: ({ rank }) => ({ rank }),
        reduce: (accumulated, { rank }) => {
            if (rank > accumulated.rank) accumulated.rank = rank
        }
    })

    index.load(
        users
            .filter((user) => user.deviceStatus)
            .map((user) => ({
                type: 'Feature' as const,
                properties: { user, rank: getRank(user) },
                geometry: {
                    type: 'Point' as const,
                    coordinates: [user.location?.longitude || 0, user.location?.latitude || 0]
                }
            }))
    )

    return index
}

export const getClusterMarkers = (index: ClusterIndex, zoom: number, highRankOnTop = false): ClusterMarker[] => {
    const markers = index.getClusters(WORLD_BBOX, Math.round(zoom)).map((feature): ClusterMarker => {
        const [lng, lat] = feature.geometry.coordinates
        const { properties } = feature

        if ('cluster' in properties) {
            return {
                key: `c_${properties.cluster_id}`,
                lat,
                lng,
                count: properties.point_count,
                rank: properties.rank,
                clusterId: properties.cluster_id
            }
        }

        return { key: `u_${properties.user.id}`, lat, lng, count: 1, rank: properties.rank, user: properties.user }
    })

    return highRankOnTop ? markers.sort((a, b) => a.rank - b.rank) : markers
}

export const resolveClusterSelection = (index: ClusterIndex, marker: ClusterMarker, zoom: number): ClusterSelection => {
    if (marker.user) return { marker, users: [marker.user] }

    const clusterId = marker.clusterId as number

    if (marker.count > CLUSTER_MEMBER_LIST_LIMIT) {
        const zoomTo = index.getClusterExpansionZoom(clusterId)
        if (zoomTo > Math.round(zoom) && zoomTo <= MAX_CLUSTER_ZOOM) return { marker, users: [], zoomTo }
    }

    return { marker, users: index.getLeaves(clusterId, Infinity).map(({ properties }) => properties.user) }
}
